// Single-loop guard for RAF-driven clocks.
// Goal: ensure at most ONE "owner" runs a persistent requestAnimationFrame loop at a time.

type LoopTick = (now: number) => void;

let activeOwner: string | null = null;
let rafId: number | null = null;
let warnedSecondOwnerThisSession = false;

// If a second owner requests while the loop is busy, keep the latest request pending.
// This prevents "lost start" during route transitions where two components briefly overlap.
let pending: { owner: string; tick: LoopTick } | null = null;

function start(owner: string, tick: LoopTick) {
  activeOwner = owner;
  warnedSecondOwnerThisSession = false;
  const loop = (now: number) => {
    // Ensure an owner is still active before executing.
    if (!rafId || activeOwner !== owner) return;
    try {
      tick(now);
    } finally {
      rafId = requestAnimationFrame(loop);
    }
  };
  rafId = requestAnimationFrame(loop);
}

/**
 * Request ownership of the singleton RAF loop.
 * - If a loop is already running, this does NOT start a second loop (returns false).
 * - The latest request is recorded as pending and will auto-start when the active owner releases.
 */
export function requestSingletonRafLoop(owner: string, tick: LoopTick): boolean {
  if (rafId != null) {
    if (import.meta.env.DEV && !warnedSecondOwnerThisSession && owner !== activeOwner) {
      warnedSecondOwnerThisSession = true;
      console.warn(
        `[singleLoopGuard] Second loop request blocked: "${owner}" while "${activeOwner ?? "unknown"}" is active.`
      );
    }
    pending = { owner, tick };
    return false;
  }
  start(owner, tick);
  return true;
}

/**
 * Release ownership of the singleton RAF loop.
 * Only the active owner can stop it.
 */
export function releaseSingletonRafLoop(owner: string): boolean {
  if (activeOwner !== owner) return false;
  if (rafId != null) cancelAnimationFrame(rafId);
  rafId = null;
  activeOwner = null;
  warnedSecondOwnerThisSession = false;

  // Auto-start the last pending requester (if any).
  const next = pending;
  pending = null;
  if (next) {
    start(next.owner, next.tick);
  }
  return true;
}

/**
 * If a component requested the loop but never became active, allow it to clear its pending request.
 */
export function cancelSingletonRafRequest(owner: string): void {
  if (pending?.owner === owner) pending = null;
}

export function getSingletonRafOwner(): string | null {
  return activeOwner;
}

export function isSingletonRafRunning(): boolean {
  return rafId != null;
}
